import type { RichBlock } from '../components/servicios/RichContent';
import type { PageIndexItem } from '../components/servicios/PageIndex';

/**
 * Prestación extraordinaria por cese de actividad (CATA Incendios) para
 * autónomos afectados por los incendios forestales de 2026, regulada por el
 * Real Decreto-ley 20/2026.
 */

export const CTA_FORMULARIO_HREF = 'https://pacientes.solimat.com/formulario-CATA-incendios-2026';
export const CTA_FORMULARIO_LABEL = 'Acceder al formulario';

export const SECTIONS: PageIndexItem[] = [
  { id: 'apoyo', label: 'Apoyo a las personas autónomas' },
  { id: 'proteccion', label: 'Qué cubre la prestación' },
  { id: 'beneficiarias', label: 'Quién puede solicitarla' },
  { id: 'documentacion', label: 'Documentación necesaria' },
  { id: 'tramitacion', label: 'Cómo se solicita' },
  { id: 'reconocimiento', label: 'Reconocimiento provisional' },
];

export const INTRO_BLOCKS: RichBlock[] = [
  {
    kind: 'p',
    text: [
      'Los trabajadores autónomos constituyen uno de los colectivos especialmente protegidos por las medidas extraordinarias aprobadas con motivo de los incendios forestales declarados como emergencia de interés nacional en determinados territorios de las provincias de Toledo y Ávila, así como de la Comunidad de Madrid.',
    ],
  },
  {
    kind: 'p',
    text: [
      'Con el objetivo de paliar las consecuencias económicas provocadas por estos acontecimientos, el ',
      { kind: 'strong', text: 'Real Decreto-ley 20/2026' },
      ' regula una prestación extraordinaria de cese de actividad destinada a aquellos trabajadores por cuenta propia cuya actividad profesional haya resultado interrumpida o gravemente afectada como consecuencia directa de los incendios o de las restricciones adoptadas por las autoridades competentes.',
    ],
  },
  {
    kind: 'p',
    text: [
      'Esta prestación tiene carácter excepcional y temporal, y pretende garantizar una cobertura económica a los autónomos mientras persistan las circunstancias que impidan el normal desarrollo de su actividad.',
    ],
  },
  {
    kind: 'p',
    text: [{ kind: 'strong', text: 'Estamos a tu lado cuando más lo necesitas.' }],
  },
];

export const PROTECCION_BLOCKS: RichBlock[] = [
  {
    kind: 'p',
    text: [
      'La prestación CATA Incendios está dirigida a personas trabajadoras autónomas que hayan tenido que cesar total o parcialmente su actividad debido a incendios forestales y a las medidas de protección civil adoptadas como consecuencia de estos.',
    ],
  },
  { kind: 'label', text: 'Entre estas medidas pueden encontrarse' },
  {
    kind: 'list',
    items: [
      { text: ['Evacuaciones.'] },
      { text: ['Confinamientos.'] },
      { text: ['Restricciones de acceso.'] },
      { text: ['Suspensión o limitación de la actividad.'] },
      { text: ['Otras medidas de naturaleza análoga.'] },
    ],
  },
  { kind: 'label', text: 'El cese de actividad podrá ser' },
  {
    kind: 'list',
    items: [
      {
        text: [{ kind: 'strong', text: 'Según su alcance' }],
        items: [
          { text: [{ kind: 'strong', text: 'Total' }, ', cuando impida desarrollar completamente la actividad.'] },
          { text: [{ kind: 'strong', text: 'Parcial' }, ', cuando afecte a un sector, centro de trabajo o parte de la actividad.'] },
        ],
      },
      {
        text: [{ kind: 'strong', text: 'Según su duración' }],
        items: [
          { text: [{ kind: 'strong', text: 'Temporal' }, ', si está prevista la reanudación de la actividad.'] },
          { text: [{ kind: 'strong', text: 'Definitivo' }, ', si la actividad no va a continuar.'] },
        ],
      },
    ],
  },
  {
    kind: 'p',
    text: [
      'La duración máxima prevista de la prestación extraordinaria es de ',
      { kind: 'strong', text: 'hasta cuatro meses' },
      ', siempre que se cumplan los requisitos establecidos legalmente.',
    ],
  },
];

export const BENEFICIARIAS_BLOCKS: RichBlock[] = [
  {
    kind: 'p',
    text: ['Podrán solicitar esta prestación las personas trabajadoras por cuenta propia incluidas en:'],
  },
  {
    kind: 'list',
    items: [
      { text: ['El Régimen Especial de Trabajadores por Cuenta Propia o Autónomos, RETA.'] },
      { text: ['El Sistema Especial para Trabajadores por Cuenta Propia Agrarios, SETA.'] },
      { text: ['Cuando corresponda legalmente, el régimen aplicable a las personas trabajadoras autónomas del mar.'] },
    ],
  },
  { kind: 'h3', text: 'Requisitos principales' },
  { kind: 'p', text: ['Con carácter general, la persona solicitante deberá:'] },
  {
    kind: 'list',
    items: [
      { text: ['Haber cesado total o parcialmente en su actividad como consecuencia de los incendios forestales.'] },
      { text: ['Estar afectada por medidas de protección civil, como evacuaciones, confinamientos, restricciones de acceso u otras equivalentes.'] },
      { text: ['Encontrarse afiliada y en alta en el régimen correspondiente en la fecha legalmente establecida o en la fecha del hecho causante, según proceda.'] },
      { text: ['Estar al corriente en el pago de las cuotas a la Seguridad Social o disponer de un aplazamiento previo reconocido por la Tesorería General de la Seguridad Social.'] },
      { text: ['Contar con la cobertura de protección por cese de actividad.'] },
      { text: ['Cumplir las obligaciones laborales aplicables, en caso de tener personas trabajadoras a su cargo.'] },
      { text: ['No percibir una prestación o pensión incompatible con la actividad por cuenta propia.'] },
      { text: ['Cumplir los restantes requisitos establecidos en la normativa reguladora.'] },
    ],
  },
  { kind: 'h3', text: 'Si el cese es definitivo' },
  {
    kind: 'p',
    text: [
      'La persona solicitante no deberá haber alcanzado la edad ordinaria de jubilación con derecho a pensión contributiva, salvo que no tenga acreditado el periodo de cotización necesario.',
    ],
  },
  { kind: 'h3', text: 'Si el cese es temporal' },
  {
    kind: 'p',
    text: [
      'Deberá mantener el alta en el régimen correspondiente durante el periodo de percepción de la prestación, de acuerdo con la normativa aplicable.',
    ],
  },
];

export const DOCUMENTACION_BLOCKS: RichBlock[] = [
  { kind: 'p', text: ['Para iniciar la tramitación deberás aportar:'] },
  { kind: 'h3', text: 'Documentación inicial' },
  {
    kind: 'list',
    items: [
      { text: ['Solicitud de la prestación, incluida la declaración responsable.'] },
      { text: ['Documento identificativo, DNI, NIE, TIE o pasaporte, preferiblemente por ambas caras cuando corresponda.'] },
      { text: ['Justificante de titularidad o cotitularidad bancaria de la cuenta indicada para recibir el abono.'] },
      { text: ['Modelo 145 de comunicación de datos al pagador, salvo en los territorios con régimen fiscal propio en los que no resulte exigible.'] },
    ],
  },
  { kind: 'h3', text: 'Documentación que debes conservar' },
  {
    kind: 'p',
    text: [
      'El reconocimiento inicial podrá realizarse sobre la base de la solicitud y de la declaración responsable. No obstante, Solimat podrá requerirte posteriormente documentación adicional, como:',
    ],
  },
  {
    kind: 'list',
    items: [
      { text: ['Acreditación de que la actividad resultó afectada por los incendios forestales.'] },
      { text: ['Documentación que justifique la imposibilidad de desarrollar la actividad.'] },
      { text: ['Certificado de estar al corriente en el pago de las cuotas a la Seguridad Social.'] },
      { text: ['Resolución de aplazamiento de cuotas, cuando corresponda.'] },
      { text: ['Libro de familia o documento equivalente, si existen hijos a cargo.'] },
      { text: ['Documentación de cotizaciones efectuadas en el extranjero.'] },
      { text: ['Resoluciones relativas a otras prestaciones o ayudas.'] },
      { text: ['Cualquier otra documentación necesaria para comprobar el cumplimiento de los requisitos.'] },
    ],
  },
];

export const DOCUMENTACION_NOTA =
  'Conserva toda la documentación relacionada con la actividad, el establecimiento, las medidas de protección civil y los efectos económicos del incendio.';

export interface PasoTramitacion {
  numero: number;
  titulo: string;
  blocks: RichBlock[];
}

export const TRAMITACION_PASOS: PasoTramitacion[] = [
  {
    numero: 1,
    titulo: 'Accede al formulario',
    blocks: [
      {
        kind: 'p',
        text: ['Entra en el Portal de Solimat y selecciona la solicitud correspondiente a CATA Incendios.'],
      },
    ],
  },
  {
    numero: 2,
    titulo: 'Completa tus datos',
    blocks: [
      { kind: 'p', text: ['Deberás informar, entre otros aspectos, sobre:'] },
      {
        kind: 'list',
        items: [
          { text: ['Datos personales y de contacto.'] },
          { text: ['Régimen de la Seguridad Social.'] },
          { text: ['Actividad económica desarrollada.'] },
          { text: ['Empresa, establecimiento o denominación comercial.'] },
          { text: ['Domicilio o domicilios en los que se desarrolla la actividad.'] },
          { text: ['Municipio y provincia afectados.'] },
          { text: ['Fecha en la que se produjo el incendio.'] },
          { text: ['Fecha de cese de actividad.'] },
          { text: ['Carácter total o parcial del cese.'] },
          { text: ['Naturaleza temporal o definitiva.'] },
          { text: ['Datos bancarios.'] },
          { text: ['Otra información necesaria para valorar la solicitud.'] },
        ],
      },
    ],
  },
  {
    numero: 3,
    titulo: 'Lee la declaración responsable',
    blocks: [
      {
        kind: 'p',
        text: [
          'La lectura y aceptación de la declaración responsable es ',
          { kind: 'strong', text: 'obligatoria' },
          '. Mediante esta declaración, la persona solicitante confirma que:',
        ],
      },
      {
        kind: 'list',
        items: [
          { text: ['Los datos facilitados son ciertos.'] },
          { text: ['Cumple los requisitos de acceso.'] },
          { text: ['Conservará y aportará la documentación cuando sea requerida.'] },
          { text: ['Comunicará cualquier cambio que afecte a la prestación.'] },
          { text: ['Reintegrará, cuando corresponda, las cantidades percibidas indebidamente.'] },
        ],
      },
    ],
  },
  {
    numero: 4,
    titulo: 'Adjunta la documentación',
    blocks: [
      {
        kind: 'p',
        text: [
          'Incorpora los documentos solicitados en formato legible, preferiblemente PDF, salvo aquellos para los que se soliciten imágenes independientes.',
        ],
      },
    ],
  },
  {
    numero: 5,
    titulo: 'Firma y presenta la solicitud',
    blocks: [
      {
        kind: 'p',
        text: ['Revisa la información, confirma los consentimientos necesarios y presenta telemáticamente la solicitud.'],
      },
    ],
  },
];

export const RECONOCIMIENTO_BLOCKS: RichBlock[] = [
  {
    kind: 'p',
    text: [
      'La prestación podrá reconocerse inicialmente con carácter provisional, tomando como referencia la información incorporada en la solicitud y en la declaración responsable.',
    ],
  },
  {
    kind: 'p',
    text: [
      'Solimat podrá revisar posteriormente el expediente y solicitar la documentación necesaria para comprobar el cumplimiento de los requisitos.',
    ],
  },
  {
    kind: 'p',
    text: [
      'Si en la revisión se detectara que no se cumplen las condiciones legalmente establecidas, podrán reclamarse las cantidades que se hubieran abonado indebidamente.',
    ],
  },
  { kind: 'h3', text: 'Recuerda comunicar cualquier cambio' },
  { kind: 'p', text: ['Durante la percepción de la prestación deberás informar a Solimat si:'] },
  {
    kind: 'list',
    items: [
      { text: ['Reanudas total o parcialmente la actividad.'] },
      { text: ['Inicias una actividad por cuenta propia o ajena.'] },
      { text: ['Accedes a otra prestación de la Seguridad Social.'] },
      { text: ['Cambian las circunstancias que motivaron la solicitud.'] },
      { text: ['Dejas de cumplir alguno de los requisitos.'] },
    ],
  },
];
